import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventManager = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    image_url: '',
    video_url: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/events`, { withCredentials: true, params: { mosque_id: user?.mosque_id } });
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(
          `${API_URL}/api/events/${editingEvent.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Event berhasil diupdate!');
      } else {
        await axios.post(
          `${API_URL}/api/events`,
          formData,
          { withCredentials: true }
        );
        toast.success('Event berhasil ditambahkan!');
      }
      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus event ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${id}`, { withCredentials: true });
      toast.success('Event berhasil dihapus!');
      fetchEvents();
    } catch (error) {
      toast.error('Gagal menghapus event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      image_url: event.image_url || '',
      video_url: event.video_url || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      image_url: '',
      video_url: ''
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Warta & Event</h2>
          <p className="text-sm font-body text-text-secondary mt-1">
            Kelola kegiatan dan berita masjid
          </p>
        </div>
        <button
          data-testid="add-event-btn"
          onClick={() => {
            setEditingEvent(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Tambah Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <CalendarIcon className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body mb-4">Belum ada event/warta</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-emerald-700 font-body font-medium hover:underline"
          >
            Tambah event pertama
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
              data-testid={`event-item-${event.id}`}
            >
              <div className="md:flex">
                {event.image_url && (
                  <div
                    className="md:w-64 h-48 bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url('${event.image_url}')` }}
                  />
                )}
                <div className="flex-1 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarIcon className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-body text-text-secondary">
                          {formatDate(event.date)}
                        </span>
                      </div>
                      <h3 className="text-xl font-body font-semibold text-text-primary mb-2">
                        {event.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm font-body text-text-secondary mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  {event.video_url && (
                    <div className="mb-4">
                      <a
                        href={event.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-body font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                        </svg>
                        Tonton Video
                      </a>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-event-${event.id}`}
                      onClick={() => handleEdit(event)}
                      className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl font-body font-medium transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      data-testid={`delete-event-${event.id}`}
                      onClick={() => handleDelete(event.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-body font-medium transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Hapus
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-heading font-bold text-primary-dark">
                  {editingEvent ? 'Edit Event' : 'Tambah Event Baru'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingEvent(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Judul Event *
                  </label>
                  <input
                    type="text"
                    data-testid="event-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Kajian Rutin Malam Jumat"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Deskripsi *
                  </label>
                  <textarea
                    data-testid="event-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    rows={4}
                    placeholder="Jelaskan detail kegiatan..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Tanggal *
                  </label>
                  <input
                    type="date"
                    data-testid="event-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    required
                  />
                </div>

                <div>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Foto Event"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    URL Video (YouTube, dll)
                  </label>
                  <input
                    type="url"
                    data-testid="event-video-input"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <p className="text-xs text-text-secondary mt-1 font-body">
                    Opsional - Link video dokumentasi
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingEvent(null);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    data-testid="event-submit-btn"
                    className="flex-1 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
                  >
                    {editingEvent ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventManager;